// [S-204] Tenant API key management hooks. Backed by the admin REST
// endpoints in apps/admin/src/routes/tenantConfig.ts:
//   POST   /tenants/:id/api-keys          → issue (plaintext secret ONCE)
//   GET    /tenants/:id/api-keys          → list metadata only
//   DELETE /tenants/:id/api-keys/:keyId   → revoke
//
// The plaintext secret is returned exactly once on creation; the
// listing endpoint never exposes it. The UI surfaces it in a copy
// affordance immediately after creation and never again.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type ApiKeyRecord = {
  id: string;
  tenantId: string;
  tenantNamespace: string;
  accessTier: "basic" | "professional" | "enterprise";
  allowedCollections: ReadonlyArray<string>;
  name: string;
  createdBy: string;
  createdAt: string; // ISO datetime
  revokedAt: string | null;
};

export type IssueApiKeyInput = {
  tenantId: string;
  name: string;
};

export type IssueApiKeyResult = {
  record: ApiKeyRecord;
  /** Plaintext secret returned ONCE — never again. */
  secret: string;
};

export const API_KEYS_KEY = ["api-keys"] as const;

export function useApiKeys(
  tenantId: string
): UseQueryResult<ApiKeyRecord[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...API_KEYS_KEY, tenantId] as const,
    enabled: tenantId.length > 0,
    queryFn: async () => {
      const res = await api<{ apiKeys: ApiKeyRecord[] }>(
        `/tenants/${encodeURIComponent(tenantId)}/api-keys`
      );
      return res.apiKeys;
    },
  });
}

export function useIssueApiKey(): UseMutationResult<
  IssueApiKeyResult,
  Error,
  IssueApiKeyInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, name }) =>
      api<IssueApiKeyResult>(
        `/tenants/${encodeURIComponent(tenantId)}/api-keys`,
        { method: "POST", body: { name } }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({
        queryKey: [...API_KEYS_KEY, tenantId],
      });
    },
  });
}

export type RevokeApiKeyInput = {
  tenantId: string;
  keyId: string;
};

export function useRevokeApiKey(): UseMutationResult<
  void,
  Error,
  RevokeApiKeyInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, keyId }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/api-keys/${encodeURIComponent(keyId)}`,
        { method: "DELETE" }
      ),
    onSuccess: (_void, { tenantId }) => {
      void qc.invalidateQueries({
        queryKey: [...API_KEYS_KEY, tenantId],
      });
    },
  });
}
